import React, { useCallback } from 'react';
import { Box, Container } from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router';

import { usePropertyRegistrationForm } from '../../hooks/use-property-registration-form';

import { NavigationPanel } from './navigation-panel';
import BasicSectionCommon from './basic-section-common';

import type { PropertyRegistrationFormProps } from '../../types/property.types';

import { useBeforeUnloadWarning } from '@/hooks/use-beforeunload-warning';
import { useSubmitting } from '@/hooks/use-submitting';
import { paths } from '@/config/paths';
import HeaderRegister from '@/features/apps/components/header-register';
import { FixedFooter } from '@/components/footer/fixed-footer';
import { useSnackbar } from '@/hooks/use-snackbar';
import { snackbarType } from '@/lib/enums';
import { MESSAGES } from '@/lib/constants';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
export const PropertiesRegistrationForm: React.FC<PropertyRegistrationFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const {
    formData,
    validationState,
    updateField,
    addFieldStaff,
    updateFieldStaff,
    removeFieldStaff,
    addContractFactory,
    updateContractFactory,
    removeContractFactory,
    handleDepartmentChange,
    getFieldError,
    isFieldValid,
    handleSubmit,
    resetForm,
    hasFormChanges,
    departments,
    departmentsLoading,
    salesStaffs,
    deliveryStaffs,
    staffsLoading,
    customers,
    constructionCompanies,
    companiesLoading,
    canManagePrice,
  } = usePropertyRegistrationForm(onSubmit);

  // Show popup warning when user tries to close/refresh page with unsaved changes
  useBeforeUnloadWarning(hasFormChanges);

  const navigate = useNavigate();
  const { setSnackbarMessage } = useSnackbar();
  const { setConfirmDialog } = useConfirmDialog();
  const { setIsLoading } = useLoadingProgress();
  const { setIsSubmitting } = useSubmitting();

  const handleFormSubmit = useCallback(async () => {
    if (handleSubmit) {
      setIsLoading(true);
      const success = await handleSubmit();
      if (success) {
        setIsSubmitting(true);
        setSnackbarMessage(MESSAGES.REGISTER_PROPERTY_SUCCESS, { type: snackbarType.SUCCESS });
        // Redirect to properties list
        navigate(paths.properties.path);
      } else {
        setIsLoading(false);
        setConfirmDialog({
          title: '登録に失敗しました。もう一度お試しください。',
        });
      }
    }
  }, [handleSubmit, navigate]);

  const handleCancel = useCallback(() => {
    // Show confirmation dialog
    navigate(paths.properties.path);
  }, [resetForm, onCancel]);

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <HeaderRegister title='現場登録フォーム' description='現場情報を入力してください' />

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Form Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <BasicSectionCommon
            formData={formData}
            updateField={updateField}
            getFieldError={getFieldError}
            isFieldValid={isFieldValid}
            addFieldStaff={addFieldStaff}
            updateFieldStaff={updateFieldStaff}
            removeFieldStaff={removeFieldStaff}
            addContractFactory={addContractFactory}
            updateContractFactory={updateContractFactory}
            removeContractFactory={removeContractFactory}
            handleDepartmentChange={handleDepartmentChange}
            departments={departments}
            departmentsLoading={departmentsLoading}
            salesStaffs={salesStaffs}
            deliveryStaffs={deliveryStaffs}
            staffsLoading={staffsLoading}
            customers={customers}
            constructionCompanies={constructionCompanies}
            companiesLoading={companiesLoading}
            canManagePrice={canManagePrice}
          />

          {/* Add bottom padding to prevent content from being hidden behind fixed footer */}
          <Box sx={{ height: '100px' }} />
          {/* Fixed Action Footer */}
          <FixedFooter
            buttons={[
              {
                label: 'キャンセル',
                onClick: handleCancel,
                variant: 'outlined',
                startIcon: <CancelIcon />,
                disabled: validationState.isSubmitting,
                sx: {
                  marginLeft: 'auto',
                },
              },
              {
                label: validationState.isSubmitting ? '登録中...' : '入力内容を決定',
                onClick: handleFormSubmit,
                variant: 'contained',
                startIcon: <SaveIcon />,
                disabled: validationState.isSubmitting || !validationState.isValid,
                loading: validationState.isSubmitting,
              },
            ]}
            isSubmitting={validationState.isSubmitting}
          />
        </Box>

        {/* Navigation Panel */}
        <Box sx={{ width: 250, flexShrink: 0 }}>
          <NavigationPanel isDetailPage={false} />
        </Box>
      </Box>
    </Container>
  );
};
